import React from 'react';

import i18n from 'util/i18n';

import { RELATIONSHIPS, RELATIONSHIPS_STATUS_MARITAL, RELATIONSHIPS_STATUS_MARITAL_PVQ } from 'config/formSections/relationships';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { Summary, NameSummary, DateSummary } from 'components/Summary';

import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { clearSameSpouse } from '@redux/modules/application/actions';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { civilUnionAddressNameFields, divorceAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import {
  RELATIONSHIPS_RELATIVES,
  FOREIGN_ACTIVITIES_REAL_ESTATE
} from 'constants/sections';
import { isPVQForm } from 'helpers/form';
import CivilUnion from './CivilUnion';
import Divorce from './Divorce';
import { RelationshipsCivilUnionModel } from 'forms/sections/relationships/marital/types';

const sectionConfig = {
  key: RELATIONSHIPS_STATUS_MARITAL.key,
  section: RELATIONSHIPS.name,
  store: RELATIONSHIPS.store,
  subsection: RELATIONSHIPS_STATUS_MARITAL.name,
  storeKey: RELATIONSHIPS_STATUS_MARITAL.storeKey,
};

export class Marital extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.divorceSummary = this.divorceSummary.bind(this);
    this.updateCivilUnion = this.updateCivilUnion.bind(this);
    this.updateDivorcedList = this.updateDivorcedList.bind(this);
    this.updateSpouse = this.updateSpouse.bind(this);
    this.updateStatus = this.updateStatus.bind(this);
    this.showCivilUnion = this.showCivilUnion.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      Status: this.props.Status,
      CivilUnion: this.props.CivilUnion,
      DivorcedList: this.props.DivorcedList,
      DivorcedSummaryComments: this.props.DivorcedSummaryComments,
      DivorcedComments: this.props.DivorcedComments,
      ...queue,
    }, [
      RELATIONSHIPS_RELATIVES,
      FOREIGN_ACTIVITIES_REAL_ESTATE
    ]);
  };

  updateStatus = (values) => {
    let updateValues = null;

    // Clear values when changing status
    switch (this.props.Status.value) {
      case 'Married':
      case 'Separated':
      case 'Spouse':
        if (window?.confirm(this.props.confirmationWindowText) !== false) {
          updateValues = {
            Status: values,
            CivilUnion: {},
          };
        }
        break;
      case 'Annulled':
      case 'Divorced':
      case 'Widowed':
        if (window?.confirm(this.props.confirmationWindowText) !== false) {
          updateValues = {
            Status: values,
            DivorcedComments: {},
            DivorcedList: Accordion.makeItemList(),
          };
        }
        break;
      default: // Covers case 'NeverMarried' or deselection
        updateValues = {
          Status: values,
          DivorcedComments: {},
          CivilUnion: {},
          DivorcedList: Accordion.makeItemList(),
        };
        break;
    }

    if (updateValues) {
      this.update({
        ...updateValues,
      });
    }
  };

  updateCivilUnion = (values: RelationshipsCivilUnionModel) => {
    if (values.Divorced.value === "No") {
      this.update({
        DivorcedList: {},
        DivorcedSummaryComments: {},
        DivorcedComments: {},
        CivilUnion: values,
      });
    } else {
      this.update({
        CivilUnion: values,
      });
    }
  };

  updateDivorcedList = (values) => {
    this.update({
      DivorcedList: values,
    });
  };

  componentDidUpdate(prevProps) {
    // Clear CivilUnion SSN when field is hidden
    if (!isEqual(prevProps.Status, this.props.Status)
      && (
        this.props.CivilUnion?.SSN?.first
        || this.props.CivilUnion?.SSN?.middle
        || this.props.CivilUnion?.SSN?.last
      )
      && !this.showCivilUnion()) {
      const clearSSNCivilUnion = cloneDeep(this.props.CivilUnion);
      clearSSNCivilUnion.SSN = {
        first: '',
        last: '',
        middle: '',
        notApplicable: false,
      };

      this.update({
        CivilUnion: clearSSNCivilUnion,
      });
    }
  }

  /**
   * Listens for updates when a spouses name is updated. This is to notify
   * other parts of the app that this information has changed
   */
  updateSpouse = () => {
    this.props.dispatch(clearSameSpouse(true));
  };

  divorceSummary = (item, index) => {
    const o = (item || {}).Item || {};
    const date = DateSummary(o.DateDivorced);
    const name = NameSummary(o.Name);
    return Summary({
      type: i18n.t('relationships.civilUnion.divorce.collection.itemType'),
      index,
      left: name,
      right: date,
      placeholder: i18n.t('relationships.relatives.collection.summary.unknown'),
    });
  };

  showDivorce = () => {
    const status = (this.props.Status || {}).value;
    const divorced = ((this.props.CivilUnion || {}).Divorced || {}).value;

    if (['Married', 'Spouse', 'Separated'].includes(status)) {
      return divorced === 'Yes';
    }

    if (['Annulled', 'Divorced', 'Widowed'].includes(status)) {
      return true;
    }

    return false;
  };

  showCivilUnion() {
    return (['Married', 'Spouse', 'Separated'].includes(this.props.Status.value));
  }

  updateDivorcedSummaryComments = (values) => {
    this.update({
      DivorcedSummaryComments: values,
    });
  };

  updateDivorcedComments = (values) => {
    this.update({
      DivorcedComments: values,
    });
  };

  divorcedComment = () => (
    <Comments
      title={i18n.t('relationships.comments.optional')}
      name="DivorcedComments"
      className="optional-comment"
      attribute="DivorcedComments"
      {...this.props.DivorcedComments}
      onUpdate={this.updateDivorcedComments}
      required={false}
    />
  );

  render() {
    const {
      DivorcedList,
      peopleWhoKnowYou,
      requireRelationshipMaritalForeignBornDocExpiration,
      requireRelationshipMaritalDivorcePhoneNumber,
      errors,
    } = this.props;

    const accordionErrors = errors && errors.filter((e) => e.indexOf('DivorcedList.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(DivorcedList?.items, peopleWhoKnowYou, divorceAddressNameFields);
    const duplicatePeopleCivilUnion = getDuplicateNamesAndAddresses([this.props.CivilUnion], peopleWhoKnowYou, civilUnionAddressNameFields);

    const duplicatePeopleCivilUnionError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            // @ts-ignore
            ...duplicatePeopleCivilUnion.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleCivilUnionWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleCivilUnion.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div
        className="section-content marital"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_STATUS_MARITAL_PVQ.key}
      >
        <h1 className="section-header">{i18n.t('relationships.pvq.marital.sectionTitleAB.title')}</h1>

        {i18n.m('relationships.pvq.marital.para.intro')}

        <Field
          title="What is your current relationship status?"
          scrollIntoView={this.props.scrollIntoView}
          attribute="Status"
        >
          <RadioGroup
            name="status"
            attribute="Status"
            className="status-options option-list option-list-vertical"
            selectedValue={this.props.Status.value}
            required={this.props.required}
            onError={this.handleError}
          >
            <Radio
              label="Single (never in a civil marriage, civil union, domestic partnership, or common law marriage)"
              className="status-never"
              value="NeverMarried"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            {/* Married, Spouse, and Separated are grouped together */}
            <Radio
              label="In a civil marriage, civil union, domestic partnership, or common law marriage"
              className="status-married"
              value="Married"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label="In a committed, spouse-like relationship"
              className="status-spouse"
              value="Spouse"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label="Legally separated"
              className="status-separated"
              value="Separated"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label="Divorced/Dissolved"
              className="status-divorced"
              value="Divorced"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label="Annulled"
              className="status-annulled"
              value="Annulled"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label="Widowed"
              className="status-widowed"
              value="Widowed"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
          </RadioGroup>

        </Field>

        {this.showCivilUnion() && (
          <div>
            <CivilUnion
              name="civilUnion"
              attribute="CivilUnion"
              {...this.props.CivilUnion}
              maritalStatus={this.props?.Status?.value}
              onUpdate={this.updateCivilUnion} // eslint-disable-line @typescript-eslint/unbound-method
              applicantBirthdate={this.props.applicantBirthdate}
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              onSpouseUpdate={this.updateSpouse} // eslint-disable-line @typescript-eslint/unbound-method
              addressBooks={this.props.addressBooks}
              currentAddress={this.props.currentAddress}
              dispatch={this.props.dispatch}
              defaultState={this.props.defaultState}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireRelationshipMaritalForeignBornDocExpiration={
                requireRelationshipMaritalForeignBornDocExpiration
              }
              formType={this.props.formType}
              divorcedComment={this.divorcedComment}
            />
            {(duplicatePeopleCivilUnion.errors.length > 0 && duplicatePeopleCivilUnionError)
              || (duplicatePeopleCivilUnion.warnings.length > 0 && duplicatePeopleCivilUnionWarning)}
          </div>
        )}
        {this.showDivorce() && (
          <div>
            <Accordion
              attribute="DivorcedList"
              defaultState={this.props.defaultState}
              scrollToBottom={this.props.scrollToBottom}
              {...this.props.DivorcedList}
              maritalStatus={this.props?.Status?.value}
              onUpdate={this.updateDivorcedList} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              required={this.props.required}
              errors={accordionErrors}
              scrollIntoView={this.props.scrollIntoView}
              summary={this.divorceSummary} // eslint-disable-line @typescript-eslint/unbound-method
              description={i18n.t('relationships.pvq.civilUnion.divorce.collection.description')}
              appendTitle={i18n.t('relationships.pvq.civilUnion.divorce.collection.appendTitle')}
              appendLabel={i18n.t('relationships.civilUnion.divorce.collection.appendLabel')}
              additionalErrors={
                (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
              }
            >
              <Divorce
                name="Item"
                attribute="Divorced"
                formType={this.props.formType}
                maritalStatus={this.props?.Status?.value}
                bind
                applicantBirthdate={this.props.applicantBirthdate}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                requireRelationshipMaritalDivorcePhoneNumber={
                  requireRelationshipMaritalDivorcePhoneNumber
                }
              />
            </Accordion>
          </div>
        )}
      </div>
      // )
    ) : ( // SF86
      <div
        className="section-content marital"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_STATUS_MARITAL.key}
      >
        <h1 className="section-header">{i18n.t('relationships.marital.sectionTitle.title')}</h1>

        <Field
          title={i18n.t('relationships.marital.heading.title')}
          scrollIntoView={this.props.scrollIntoView}
          attribute="Status"
        >
          <RadioGroup
            name="status"
            attribute="Status"
            className="status-options option-list option-list-vertical"
            selectedValue={this.props.Status.value}
            required={this.props.required}
            onError={this.handleError}
          >
            <Radio
              label={i18n.t('relationships.marital.label.status.never')}
              className="status-never"
              value="NeverMarried"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label={i18n.t('relationships.marital.label.status.married')}
              className="status-married"
              value="Married"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label={i18n.t('relationships.marital.label.status.separated')}
              className="status-separated"
              value="Separated"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label={i18n.t('relationships.marital.label.status.annulled')}
              className="status-annulled"
              value="Annulled"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label={i18n.t('relationships.marital.label.status.divorced')}
              className="status-divorced"
              value="Divorced"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
            <Radio
              label={i18n.t('relationships.marital.label.status.widowed')}
              className="status-widowed"
              value="Widowed"
              onUpdate={this.updateStatus} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError}
            />
          </RadioGroup>
        </Field>

        <Show when={this.showCivilUnion()}>
          <CivilUnion
            name="civilUnion"
            attribute="CivilUnion"
            {...this.props.CivilUnion}
            maritalStatus={this.props?.Status?.value}
            onUpdate={this.updateCivilUnion} // eslint-disable-line @typescript-eslint/unbound-method
            applicantBirthdate={this.props.applicantBirthdate}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            onSpouseUpdate={this.updateSpouse} // eslint-disable-line @typescript-eslint/unbound-method
            addressBooks={this.props.addressBooks}
            currentAddress={this.props.currentAddress}
            dispatch={this.props.dispatch}
            defaultState={this.props.defaultState}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireRelationshipMaritalForeignBornDocExpiration={
              requireRelationshipMaritalForeignBornDocExpiration
            }
            formType={this.props.formType}
            divorcedComment={this.divorcedComment}
          />
          {(duplicatePeopleCivilUnion.errors.length > 0 && duplicatePeopleCivilUnionError)
            || (duplicatePeopleCivilUnion.warnings.length > 0 && duplicatePeopleCivilUnionWarning)}
        </Show>
        <Show when={this.showDivorce()}>
          <p>{i18n.t('relationships.civilUnion.divorce.para.intro')}</p>
          <Accordion
            attribute="DivorcedList"
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            {...this.props.DivorcedList}
            onUpdate={this.updateDivorcedList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            errors={accordionErrors}
            scrollIntoView={this.props.scrollIntoView}
            summary={this.divorceSummary} // eslint-disable-line @typescript-eslint/unbound-method
            description={i18n.t('relationships.civilUnion.divorce.collection.description')}
            appendTitle={i18n.t('relationships.civilUnion.divorce.collection.appendTitle')}
            appendLabel={i18n.t('relationships.civilUnion.divorce.collection.appendLabel')}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <Divorce
              name="Item"
              attribute="Divorced"
              bind
              applicantBirthdate={this.props.applicantBirthdate}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireRelationshipMaritalDivorcePhoneNumber={
                requireRelationshipMaritalDivorcePhoneNumber
              }
            />
          </Accordion>
          <Comments
            title={i18n.t('relationships.comments.summary')}
            name="DivorcedSummaryComments"
            attribute="DivorcedSummaryComments"
            className="summary-comment"
            {...this.props.DivorcedSummaryComments}
            onUpdate={this.updateDivorcedSummaryComments}
            onError={this.props.onError}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

Marital.defaultProps = {
  Status: {},
  CivilUnion: {},
  DivorcedList: Accordion.makeItemList(),
  DivorcedComments: {},
  DivorcedSummaryComments: {},
  confirmationWindowText: i18n.t('relationships.marital.confirmation'),
  onUpdate: () => { },
  onError: (value, arr) => arr,
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '.bottom-btns',
  scrollIntoView: false,
};

export default connectSubsection(Marital, sectionConfig);
